import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getExerciseProgressHistory, getExerciseChartData, updateExerciseLog, deleteSessionExerciseLogs, toggleExerciseLogExcluded } from '../lib/database';
import { calculate1RM, getWeightRangeForReps, generateWeightSuggestion } from '../lib/progressUtils';
import { useSwipeBack } from '../hooks/useSwipeBack';
import ProgressChart from './ProgressChart';
import './ExerciseProgress.css';

function ExerciseProgress() {
  const navigate = useNavigate();
  const { exerciseId } = useParams();
  const location = useLocation();
  const { profile } = useAuth();
  const toast = useToast();
  const [history, setHistory] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [exerciseName, setExerciseName] = useState('');
  const [loading, setLoading] = useState(true);

  // History accordion state
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const PREVIEW_COUNT = 3;

  // Edit state
  const [editingSession, setEditingSession] = useState(null);
  const [editData, setEditData] = useState([]);

  // Menu & UI state
  const [menuOpen, setMenuOpen] = useState(null); // session index with menu open
  const [deletingSession, setDeletingSession] = useState(null);
  const [notesExpanded, setNotesExpanded] = useState(null); // session index with notes open

  // Close menu on outside click
  const menuRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuOpen !== null && menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [menuOpen]);

  useEffect(() => {
    const name = location.state?.exerciseName || 'Esercizio';
    setExerciseName(name);

    if (profile?.id && exerciseId) {
      loadData();
    }
  }, [profile, exerciseId, location]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [historyData, chartDataResult] = await Promise.all([
        getExerciseProgressHistory(profile.id, exerciseId, 20),
        getExerciseChartData(profile.id, exerciseId, 20)
      ]);
      setHistory(historyData);
      setChartData(chartDataResult);
    } catch (error) {
      console.error('Error loading exercise data:', error);
      setHistory([]);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter included sessions for calculations
  const includedHistory = useMemo(() => history.filter(s => !s.excluded), [history]);

  // Detect if this is a cardio exercise
  const isCardio = useMemo(() => {
    return history.length > 0 && history[0]?.isCardio === true;
  }, [history]);

  // Calculate record from included history
  const stats = useMemo(() => {
    if (history.length === 0) return null;

    if (isCardio) {
      // Cardio stats: best distance, longest duration, best speed
      let bestDistance = 0;
      let bestDistanceDate = null;
      let longestDuration = 0;
      let longestDurationDate = null;
      let bestSpeed = 0;

      includedHistory.forEach(session => {
        session.sets.forEach(set => {
          if (set.distance && set.distance > bestDistance) {
            bestDistance = set.distance;
            bestDistanceDate = session.date;
          }
          if (set.duration && set.duration > longestDuration) {
            longestDuration = set.duration;
            longestDurationDate = session.date;
          }
          if (set.speed && set.speed > bestSpeed) {
            bestSpeed = set.speed;
          }
        });
      });

      const lastSession = includedHistory[0];
      const lastSet = lastSession?.sets[0];

      return {
        isCardio: true,
        bestDistance: bestDistance > 0 ? { distance: bestDistance, date: bestDistanceDate } : null,
        longestDuration: longestDuration > 0 ? { duration: longestDuration, date: longestDurationDate } : null,
        bestSpeed: bestSpeed > 0 ? bestSpeed : null,
        lastPerformed: lastSet ? {
          duration: lastSet.duration,
          distance: lastSet.distance,
          speed: lastSet.speed,
          date: lastSession?.date
        } : null,
        totalSessions: history.length
      };
    } else {
      // Strength stats
      let maxWeight = 0;
      let recordSet = null;
      let recordDate = null;

      includedHistory.forEach(session => {
        session.sets.forEach(set => {
          if (set.weight && set.reps >= 3 && set.weight > maxWeight) {
            maxWeight = set.weight;
            recordSet = set;
            recordDate = session.date;
          }
        });
      });

      const lastSession = includedHistory[0];
      const lastWeight = lastSession?.sets.find(s => s.weight)?.weight || 0;
      const lastReps = lastSession?.sets.find(s => s.reps)?.reps || 0;
      const totalSessions = history.length;

      return {
        isCardio: false,
        record: recordSet ? { weight: recordSet.weight, reps: recordSet.reps, date: recordDate } : null,
        lastPerformed: lastWeight > 0 ? { weight: lastWeight, reps: lastReps, date: lastSession?.date } : null,
        totalSessions
      };
    }
  }, [history, includedHistory, isCardio]);

  // Calculate 1RM and analysis (only for strength exercises)
  const analysis = useMemo(() => {
    if (isCardio || !stats?.record) return null;
    const estimated1RM = calculate1RM(stats.record.weight, stats.record.reps);
    if (!estimated1RM) return null;

    const repRanges = [5, 8, 10, 12].map(reps => {
      const range = getWeightRangeForReps(estimated1RM, reps);
      return range ? { reps, ...range } : null;
    }).filter(Boolean);

    return { estimated1RM: Math.round(estimated1RM * 10) / 10, repRanges };
  }, [stats, isCardio]);

  // Generate weight suggestion from included history (only for strength exercises)
  const suggestion = useMemo(() => {
    if (isCardio || includedHistory.length < 2) return null;
    const sessions = includedHistory.map(session => {
      const weights = session.sets.map(s => s.weight).filter(Boolean);
      const reps = session.sets.map(s => s.reps).filter(Boolean);
      return {
        weight: weights.length > 0 ? Math.max(...weights) : 0,
        reps: reps.length > 0 ? Math.round(reps.reduce((a, b) => a + b, 0) / reps.length) : 0,
        date: session.date
      };
    }).filter(s => s.weight > 0 && s.reps > 0);
    if (sessions.length < 2) return null;
    return generateWeightSuggestion(sessions);
  }, [includedHistory, isCardio]);

  const handleBack = () => {
    navigate('/athlete/progress');
  };

  const { handleTouchStart, handleTouchEnd } = useSwipeBack(handleBack);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Oggi';
    if (diffDays === 1) return 'Ieri';
    if (diffDays < 7) return `${diffDays}g fa`;
    return new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short' }).format(date);
  };

  // --- Edit handlers ---
  const handleStartEdit = (sessionIdx) => {
    setMenuOpen(null);
    const session = history[sessionIdx];
    setEditingSession(sessionIdx);
    if (isCardio) {
      setEditData(session.sets.map(set => ({
        id: set.id,
        duration: set.duration?.toString() || '',
        distance: set.distance?.toString() || '',
        speed: set.speed?.toString() || '',
        incline: set.incline?.toString() || ''
      })));
    } else {
      setEditData(session.sets.map(set => ({
        id: set.id,
        weight: set.weight?.toString() || '',
        reps: set.reps?.toString() || ''
      })));
    }
  };

  const handleCancelEdit = () => {
    setEditingSession(null);
    setEditData([]);
  };

  const handleSaveEdit = async () => {
    try {
      const session = history[editingSession];
      await Promise.all(editData.map((edit, idx) => {
        const original = session.sets[idx];
        const updates = {};

        if (isCardio) {
          const newDuration = edit.duration ? parseInt(edit.duration) : null;
          const newDistance = edit.distance ? parseFloat(edit.distance) : null;
          const newSpeed = edit.speed ? parseFloat(edit.speed) : null;
          const newIncline = edit.incline ? parseFloat(edit.incline) : null;

          if (newDuration !== original.duration) updates.duration_minutes = newDuration;
          if (newDistance !== original.distance) updates.distance = newDistance;
          if (newSpeed !== original.speed) updates.speed = newSpeed;
          if (newIncline !== original.incline) updates.incline = newIncline;
        } else {
          const newWeight = edit.weight ? parseFloat(edit.weight) : null;
          const newReps = edit.reps ? parseInt(edit.reps) : null;

          if (newWeight !== original.weight) updates.weight = newWeight;
          if (newReps !== original.reps) updates.reps = newReps;
        }

        if (Object.keys(updates).length > 0) {
          return updateExerciseLog(edit.id, updates);
        }
        return Promise.resolve();
      }));

      setEditingSession(null);
      setEditData([]);
      await loadData();
      toast.success('Modifiche salvate');
    } catch (error) {
      console.error('Error saving edit:', error);
      toast.error('Errore nel salvataggio. Riprova.');
    }
  };

  const handleUpdateEditField = useCallback((setIdx, field, value) => {
    setEditData(prev => prev.map((item, idx) =>
      idx === setIdx ? { ...item, [field]: value } : item
    ));
  }, []);

  // --- Delete handler ---
  const handleDeleteSession = async (sessionIdx) => {
    try {
      const session = history[sessionIdx];
      const logIds = session.sets.map(set => set.id).filter(Boolean);
      if (logIds.length === 0) return;

      await deleteSessionExerciseLogs(logIds);
      setDeletingSession(null);
      await loadData();
      toast.success('Sessione eliminata');
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error('Errore nell\'eliminazione. Riprova.');
    }
  };

  // --- Exclude toggle handler ---
  const handleToggleExclude = async (sessionIdx) => {
    try {
      setMenuOpen(null);
      const session = history[sessionIdx];
      const logIds = session.sets.map(set => set.id).filter(Boolean);
      if (logIds.length === 0) return;

      const newExcluded = !session.excluded;
      await toggleExerciseLogExcluded(logIds, newExcluded);
      await loadData();
      toast.success(newExcluded ? 'Sessione esclusa' : 'Sessione inclusa');
    } catch (error) {
      console.error('Error toggling exclude:', error);
      toast.error('Errore. Riprova.');
    }
  };

  // --- Menu handler ---
  const handleMenuAction = (action, sessionIdx) => {
    setMenuOpen(null);
    switch (action) {
      case 'edit':
        handleStartEdit(sessionIdx);
        break;
      case 'exclude':
        handleToggleExclude(sessionIdx);
        break;
      case 'delete':
        setDeletingSession(sessionIdx);
        break;
      default:
        break;
    }
  };

  // --- Notes helpers ---
  const sessionHasNotes = (session) => {
    return session.sets.some(set => set.notes && set.notes.trim());
  };

  const getSessionNotes = (session) => {
    return session.sets
      .filter(set => set.notes && set.notes.trim())
      .map(set => ({ setNumber: set.setNumber, notes: set.notes }));
  };

  if (loading) {
    return (
      <div className="exercise-progress-container" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <div className="progress-loading">
          <div className="loading-spinner"></div>
          <p>Caricamento dati...</p>
        </div>
      </div>
    );
  }

  const visibleHistory = historyExpanded ? history : history.slice(0, PREVIEW_COUNT);
  const hasMoreHistory = history.length > PREVIEW_COUNT;

  return (
    <div className="exercise-progress-container" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <header className="exercise-progress-header">
        <button className="btn-back-minimal" onClick={handleBack}>
          <span className="back-arrow">&larr;</span>
        </button>
        <h1 className="exercise-progress-title">{exerciseName}</h1>
      </header>

      <main className="exercise-progress-main">
        {/* Chart Section */}
        {chartData.length >= 2 && (
          <section className="section-card chart-section">
            <h2 className="section-title">{isCardio ? 'Andamento Distanza' : 'Andamento Peso'}</h2>
            <ProgressChart data={chartData} recordWeight={stats?.record?.weight} isCardio={isCardio} />
          </section>
        )}

        {/* Stats Row */}
        {stats && (
          <section className="stats-row">
            {isCardio ? (
              <>
                {stats.bestDistance && (
                  <div className="stat-card stat-record">
                    <span className="stat-label">Max Distanza</span>
                    <span className="stat-value">{stats.bestDistance.distance}km</span>
                    <span className="stat-detail">record</span>
                  </div>
                )}
                {stats.longestDuration && (
                  <div className="stat-card stat-last">
                    <span className="stat-label">Max Durata</span>
                    <span className="stat-value">{stats.longestDuration.duration}min</span>
                    <span className="stat-detail">record</span>
                  </div>
                )}
                {stats.bestSpeed && (
                  <div className="stat-card stat-sessions">
                    <span className="stat-label">Max Velocità</span>
                    <span className="stat-value">{stats.bestSpeed}km/h</span>
                    <span className="stat-detail">record</span>
                  </div>
                )}
              </>
            ) : (
              <>
                {stats.record && (
                  <div className="stat-card stat-record">
                    <span className="stat-label">Record</span>
                    <span className="stat-value">{stats.record.weight}kg</span>
                    <span className="stat-detail">&times; {stats.record.reps} reps</span>
                  </div>
                )}
                {stats.lastPerformed && (
                  <div className="stat-card stat-last">
                    <span className="stat-label">Ultimo</span>
                    <span className="stat-value">{stats.lastPerformed.weight}kg</span>
                    <span className="stat-detail">&times; {stats.lastPerformed.reps} reps</span>
                  </div>
                )}
              </>
            )}
            <div className="stat-card stat-sessions">
              <span className="stat-label">Sessioni</span>
              <span className="stat-value">{stats.totalSessions}</span>
              <span className="stat-detail">totali</span>
            </div>
          </section>
        )}

        {/* 1RM Analysis Section */}
        {analysis && (
          <section className="section-card analysis-section">
            <h2 className="section-title">Analisi</h2>
            <div className="analysis-1rm">
              <div className="analysis-1rm-label">1RM stimato</div>
              <div className="analysis-1rm-value">~{analysis.estimated1RM}kg</div>
              <div className="analysis-1rm-note">Media Epley/Brzycki &middot; Peso totale (incl. bilanciere)</div>
            </div>
            <div className="analysis-percentages">
              <div className="percentages-header">
                <span>Reps</span>
                <span>%</span>
                <span>Peso</span>
              </div>
              {analysis.repRanges.map(range => (
                <div key={range.reps} className="percentages-row">
                  <span className="reps-col">{range.reps} reps</span>
                  <span className="percent-col">{range.percentage}%</span>
                  <span className="weight-col">{range.min}-{range.max}kg</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Suggestion Section */}
        {suggestion?.hasSuggestion && (
          <section className="section-card suggestion-section">
            <div className="suggestion-header">
              <span className="suggestion-icon">&#128161;</span>
              <h2 className="section-title">Suggerimento</h2>
            </div>
            <div className="suggestion-content">
              {suggestion.isStable && suggestion.progressionNote && (
                <p className="suggestion-status">
                  Ultime sessioni stabili a {suggestion.lastWeight}kg
                </p>
              )}
              <div className="suggestion-main">
                <span className="suggestion-label">Prova</span>
                <span className="suggestion-weight">
                  {suggestion.weightRange.min}-{suggestion.weightRange.max}kg
                </span>
                <span className="suggestion-reps">
                  &times; {suggestion.targetReps} reps
                </span>
              </div>
              {suggestion.progressionNote && (
                <p className="suggestion-note">
                  Microprogressione: +2.5kg rispetto all'ultima sessione
                </p>
              )}
            </div>
          </section>
        )}

        {/* History Section - Collapsible */}
        {history.length > 0 ? (
          <section className="section-card history-section">
            <div
              className="history-header-toggle"
              onClick={() => setHistoryExpanded(!historyExpanded)}
            >
              <h2 className="section-title">Storico Sessioni ({history.length})</h2>
              <span className={`toggle-arrow ${historyExpanded ? 'toggle-arrow--open' : ''}`}>
                &#9660;
              </span>
            </div>

            <div className="history-list">
              {visibleHistory.map((session, idx) => (
                <div
                  key={idx}
                  className={`history-item ${session.excluded ? 'history-item--excluded' : ''}`}
                >
                  {/* Delete confirmation */}
                  {deletingSession === idx && (
                    <div className="history-delete-confirm">
                      <p>Eliminare questa sessione?</p>
                      <div className="history-delete-actions">
                        <button className="btn-cancel-sm" onClick={() => setDeletingSession(null)}>
                          Annulla
                        </button>
                        <button className="btn-delete-sm" onClick={() => handleDeleteSession(idx)}>
                          Elimina
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Normal compact view */}
                  {deletingSession !== idx && editingSession !== idx && (
                    <>
                      <div className="history-row">
                        <div className="history-row-left">
                          <span className="history-date">{formatDate(session.date)}</span>
                          {session.excluded && (
                            <span className="history-excluded-badge">esclusa</span>
                          )}
                        </div>
                        <div className="history-row-right">
                          {sessionHasNotes(session) && (
                            <button
                              className={`history-note-btn ${notesExpanded === idx ? 'history-note-btn--active' : ''}`}
                              onClick={() => setNotesExpanded(notesExpanded === idx ? null : idx)}
                              title="Note"
                            >
                              &#128221;
                            </button>
                          )}
                          <div className="history-menu-wrap" ref={menuOpen === idx ? menuRef : null}>
                            <button
                              className="history-menu-btn"
                              onClick={() => setMenuOpen(menuOpen === idx ? null : idx)}
                            >
                              &middot;&middot;&middot;
                            </button>
                            {menuOpen === idx && (
                              <div className="history-menu-dropdown">
                                <button onClick={() => handleMenuAction('edit', idx)}>
                                  &#9998; Modifica
                                </button>
                                <button onClick={() => handleMenuAction('exclude', idx)}>
                                  {session.excluded ? '&#9673; Includi nel grafico' : '&#9675; Escludi dal grafico'}
                                </button>
                                <button
                                  className="history-menu-delete"
                                  onClick={() => handleMenuAction('delete', idx)}
                                >
                                  &#128465; Elimina
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="history-sets">
                        {isCardio ? (
                          session.sets.map((set, setIdx) => (
                            <span key={setIdx} className="history-set history-set--cardio">
                              {set.duration ? `${set.duration}min` : ''}
                              {set.distance ? ` · ${set.distance}km` : ''}
                              {set.speed ? ` · ${set.speed}km/h` : ''}
                            </span>
                          ))
                        ) : (
                          session.sets.map((set, setIdx) => (
                            <span key={setIdx} className="history-set">
                              {set.weight || '-'}kg&times;{set.reps || '-'}
                            </span>
                          ))
                        )}
                      </div>
                      {/* Expanded notes */}
                      {notesExpanded === idx && (
                        <div className="history-notes-panel">
                          {getSessionNotes(session).map((n, nIdx) => (
                            <div key={nIdx} className="history-note-item">
                              <span className="history-note-set">S{n.setNumber}</span>
                              <span className="history-note-text">{n.notes}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* Edit view - compact */}
                  {editingSession === idx && (
                    <div className="history-edit">
                      <div className="history-edit-header">
                        <span className="history-edit-title">Modifica &middot; {formatDate(session.date)}</span>
                      </div>
                      <div className="history-edit-grid">
                        {editData.map((setEdit, setIdx) => (
                          <div key={setIdx} className="history-edit-cell">
                            {isCardio ? (
                              <>
                                <div className="history-edit-inputs history-edit-inputs--cardio">
                                  <input
                                    type="number"
                                    inputMode="numeric"
                                    value={setEdit.duration}
                                    onChange={(e) => handleUpdateEditField(setIdx, 'duration', e.target.value)}
                                    placeholder="min"
                                    className="history-edit-input"
                                  />
                                  <span className="history-edit-unit">min</span>
                                  <input
                                    type="number"
                                    inputMode="decimal"
                                    value={setEdit.distance}
                                    onChange={(e) => handleUpdateEditField(setIdx, 'distance', e.target.value)}
                                    placeholder="km"
                                    step="0.1"
                                    className="history-edit-input"
                                  />
                                  <span className="history-edit-unit">km</span>
                                  <input
                                    type="number"
                                    inputMode="decimal"
                                    value={setEdit.speed}
                                    onChange={(e) => handleUpdateEditField(setIdx, 'speed', e.target.value)}
                                    placeholder="km/h"
                                    step="0.5"
                                    className="history-edit-input"
                                  />
                                  <span className="history-edit-unit">km/h</span>
                                </div>
                              </>
                            ) : (
                              <>
                                <span className="history-edit-label">S{setIdx + 1}</span>
                                <div className="history-edit-inputs">
                                  <input
                                    type="number"
                                    inputMode="decimal"
                                    value={setEdit.weight}
                                    onChange={(e) => handleUpdateEditField(setIdx, 'weight', e.target.value)}
                                    placeholder="kg"
                                    step="0.5"
                                    className="history-edit-input"
                                  />
                                  <span className="history-edit-x">&times;</span>
                                  <input
                                    type="number"
                                    inputMode="numeric"
                                    value={setEdit.reps}
                                    onChange={(e) => handleUpdateEditField(setIdx, 'reps', e.target.value)}
                                    placeholder="reps"
                                    className="history-edit-input"
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="history-edit-actions">
                        <button className="btn-cancel-sm" onClick={handleCancelEdit}>
                          Annulla
                        </button>
                        <button className="btn-save-sm" onClick={handleSaveEdit}>
                          Salva
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Show more/less toggle */}
            {hasMoreHistory && (
              <button
                className="history-toggle-btn"
                onClick={() => setHistoryExpanded(!historyExpanded)}
              >
                {historyExpanded
                  ? 'Nascondi storico'
                  : `Mostra tutto (${history.length - PREVIEW_COUNT} in pi\u00F9)`
                }
              </button>
            )}
          </section>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">&#128202;</div>
            <h3>Nessuna sessione registrata</h3>
            <p>Completa un allenamento con questo esercizio per vedere i tuoi progressi</p>
          </div>
        )}
      </main>
    </div>
  );
}

export default ExerciseProgress;
